import React, { useState } from 'react';

interface ContactFormData {
    name: string;
    email: string;
    company: string;
    phone: string;
    userType: string;
    subject: string;
    message: string;
}

const ContactUs: React.FC = () => {
    const [formData, setFormData] = useState<ContactFormData>({
        name: '',
        email: '',
        company: '',
        phone: '',
        userType: '',
        subject: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus('idle');
        
        try {
            const response = await fetch('/api/contact-working', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            const result = await response.json();

            if (response.ok) {
                setSubmitStatus('success');
                // Reset form on successful submission
                setFormData({
                    name: '',
                    email: '',
                    company: '',
                    phone: '',
                    userType: '',
                    subject: '',
                    message: ''
                });
            } else {
                console.error('Form submission error:', result.error);
                setSubmitStatus('error');
            }
        } catch (error) {
            console.error('Network error:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-700 text-white py-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h1 className="text-4xl md:text-5xl font-bold mb-6">
                        Contact Our Energy Retrofit Experts
                    </h1>
                    <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                        Get personalized assistance with your NYC building energy compliance and retrofit projects
                    </p>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                    {/* Contact Form */}
                    <div className="bg-white rounded-lg shadow-lg p-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
                        
                        {submitStatus === 'success' && (
                            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <span className="text-green-400 text-xl">✓</span>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm font-medium text-green-800">
                                            Message sent successfully! We'll get back to you within 24 hours.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                        Full Name *
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        required
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                        Email Address *
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        required
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                                        Company/Organization
                                    </label>
                                    <input
                                        type="text"
                                        id="company"
                                        name="company"
                                        value={formData.company}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                                        Phone Number
                                    </label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="userType" className="block text-sm font-medium text-gray-700 mb-1">
                                    I am a... *
                                </label>
                                <select
                                    id="userType"
                                    name="userType"
                                    required
                                    value={formData.userType}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="">Select your role</option>
                                    <option value="building-owner">Building Owner/Property Manager</option>
                                    <option value="contractor">Contractor/Service Provider</option>
                                    <option value="lender">Lender/Financial Partner</option>
                                    <option value="consultant">Energy Consultant/Professional</option>
                                    <option value="tenant">Tenant</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            <div>
                                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                                    Subject *
                                </label>
                                <input
                                    type="text"
                                    id="subject"
                                    name="subject"
                                    required
                                    value={formData.subject}
                                    onChange={handleInputChange}
                                    placeholder="e.g., LL97 Compliance Question, Partnership Inquiry, Technical Support"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                                    Message *
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    required
                                    rows={6}
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    placeholder="Please describe your inquiry or project details..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full bg-blue-600 text-white py-3 px-6 rounded-md font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? 'Sending...' : 'Send Message'}
                            </button>
                        </form>
                    </div>

                    {/* Contact Information */}
                    <div className="space-y-8">
                        {/* Contact Details */}
                        <div className="bg-white rounded-lg shadow-lg p-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">Get in Touch</h2>
                            <div className="space-y-4">
                                <div className="flex items-start space-x-3">
                                    <div className="text-blue-600 text-xl">📧</div>
                                    <div>
                                        <h3 className="font-medium text-gray-900">Email</h3>
                                        <p className="text-gray-600">info@ll97plan.com</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <div className="text-blue-600 text-xl">📞</div>
                                    <div>
                                        <h3 className="font-medium text-gray-900">Phone</h3>
                                        <p className="text-gray-600">917-674-5720</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Quick Links */}
                        <div className="bg-white rounded-lg shadow-lg p-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick Actions</h2>
                            <div className="space-y-4">
                                <a 
                                    href="/ll97-calculator" 
                                    className="flex items-center space-x-3 p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors"
                                >
                                    <span className="text-xl">💰</span>
                                    <div>
                                        <h3 className="font-medium text-gray-900">Calculate LL97 Fines</h3>
                                        <p className="text-sm text-gray-600">Estimate potential fines for your building</p>
                                    </div>
                                </a>
                                <a 
                                    href="/retrofit-calculator" 
                                    className="flex items-center space-x-3 p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors"
                                >
                                    <span className="text-xl">🔧</span>
                                    <div>
                                        <h3 className="font-medium text-gray-900">Retrofit Analysis</h3>
                                        <p className="text-sm text-gray-600">Analyze retrofit opportunities</p>
                                    </div>
                                </a>
                                <a 
                                    href="/contractor-partnership-signup" 
                                    className="flex items-center space-x-3 p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors"
                                >
                                    <span className="text-xl">🤝</span>
                                    <div>
                                        <h3 className="font-medium text-gray-900">Partner With Us</h3>
                                        <p className="text-sm text-gray-600">Join our contractor network</p>
                                    </div>
                                </a>
                            </div>
                        </div>

                        {/* FAQ */}
                        <div className="bg-white rounded-lg shadow-lg p-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
                            <div className="space-y-4">
                                <div>
                                    <h3 className="font-medium text-gray-900 mb-2">How accurate are the LL97 fine calculations?</h3>
                                    <p className="text-sm text-gray-600">Our calculations are based on official NYC Local Law 97 formulas and current carbon emission factors.</p>
                                </div>
                                <div>
                                    <h3 className="font-medium text-gray-900 mb-2">Is this service free?</h3>
                                    <p className="text-sm text-gray-600">Basic analysis tools are free. Advanced consulting services are available for comprehensive retrofit planning.</p>
                                </div>
                                <div>
                                    <h3 className="font-medium text-gray-900 mb-2">Do you work with buildings outside NYC?</h3>
                                    <p className="text-sm text-gray-600">Currently, our tools are specifically designed for NYC Local Law 97 compliance.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContactUs;
